import React, { useState, useEffect } from 'react';
import { administrationService } from '../../services/api/administrationService';
import type { Administration } from '../../types/administration.types';
import { toast } from 'react-toastify';

const AdministrationView: React.FC = () => {
  const [secretariatOfficers, setSecretariatOfficers] = useState<Administration[]>([]);
  const [headOfficeOfficers, setHeadOfficeOfficers] = useState<Administration[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllOfficers();
  }, []);

  const fetchAllOfficers = async () => {
    try {
      setLoading(true);
      const [secretariatResponse, headOfficeResponse] = await Promise.all([
        administrationService.getSecretariatOfficers(),
        administrationService.getHeadOfficeOfficers(),
      ]);

      setSecretariatOfficers(secretariatResponse.data || []);
      setHeadOfficeOfficers(headOfficeResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch administration officers:', error);
      toast.error('Failed to load administration information');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading administration information...</p>
        </div>
      </div>
    );
  }

  return (
    <section className="min-h-screen bg-gray-50 py-8">
      {/* Page Header */}
      <div className="container mx-auto px-4 lg:px-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 pb-2 border-b-4 border-blue-600 inline-block">
            ADMINISTRATION
          </h2>
        </div>
      </div>

      {/* Secretariat Section */}
      <div className="container mx-auto px-4 lg:px-8 mb-12">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4">
            <h3 className="text-white font-bold text-lg">
              ADMINISTRATION -- Telangana Secretariat
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider w-24">
                    Sl.NO.
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider min-w-[200px]">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                    Designation
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                    Office
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {secretariatOfficers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                      No secretariat officers found
                    </td>
                  </tr>
                ) : (
                  secretariatOfficers.map((officer) => (
                    <tr key={officer.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {officer.serial_number}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                        {officer.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {officer.designation}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {officer.office_address}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Head Office Section */}
      <div className="container mx-auto px-4 lg:px-8 mb-12">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-green-900 to-green-700 px-6 py-4">
            <h3 className="text-white font-bold text-lg">HEAD OFFICE</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider w-24">
                    Sl.NO.
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider min-w-[180px]">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                    Designation
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                    Office Address
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider min-w-[150px]">
                    Contact
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {headOfficeOfficers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                      No head office officers found
                    </td>
                  </tr>
                ) : (
                  headOfficeOfficers.map((officer) => (
                    <tr key={officer.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {officer.serial_number}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                        {officer.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {officer.designation}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {officer.office_address}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {officer.contact && <div className="mb-1">{officer.contact}</div>}
                        {officer.email && (
                          <a
                            href={`mailto:${officer.email}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline text-xs"
                          >
                            {officer.email}
                          </a>
                        )}
                        {!officer.contact && !officer.email && (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border-2 border-blue-200 rounded-lg p-6 shadow-sm">
            <div className="text-sm font-semibold text-gray-600 uppercase">Total Officers</div>
            <div className="text-4xl font-bold text-blue-900 mt-3">
              {secretariatOfficers.length + headOfficeOfficers.length}
            </div>
          </div>
          <div className="bg-white border-2 border-purple-200 rounded-lg p-6 shadow-sm">
            <div className="text-sm font-semibold text-gray-600 uppercase">Secretariat</div>
            <div className="text-4xl font-bold text-purple-900 mt-3">
              {secretariatOfficers.length}
            </div>
          </div>
          <div className="bg-white border-2 border-green-200 rounded-lg p-6 shadow-sm">
            <div className="text-sm font-semibold text-gray-600 uppercase">Head Office</div>
            <div className="text-4xl font-bold text-green-900 mt-3">
              {headOfficeOfficers.length}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdministrationView;